import { PrinterOutlined, UploadOutlined } from "@ant-design/icons";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { Button, Col, Modal } from "antd";
import { useCallback, useState } from "react";
import { exportToCSV, exportToXLSX, type TExportToCSV } from "std/csv";
import { useRelatorioStore } from "../store";

export function CtrlBar<T = unknown, TChild = unknown>({
	csvData,
	registros,
	refetch,
	noFilter = false,
	isEmptyResponse = false,
	extraButtons,
	onImprimir,
}: {
	csvData: TExportToCSV<T, TChild> | undefined;
	registros: T[];
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<T, Error>>;
	noFilter?: boolean;
	isEmptyResponse?: boolean;
	extraButtons?;
	onImprimir?: () => void;
}): JSX.Element {
	const isRelatorioRequisitado = useRelatorioStore((s) => s.isRelatorioRequisitado);
	const isImpressaoRequisitado = useRelatorioStore((s) => s.isImpressaoRequisitado);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [exporting, setExporting] = useState(false);

	const handleGerar = () => {
		useRelatorioStore.setState({ isRelatorioRequisitado: true });

		refetch({});
	};

	const handleImprimir = () => {
		if (onImprimir) {
			onImprimir();
			return;
		}

		useRelatorioStore.setState({ isImpressaoRequisitado: true });
	};

	const handleExportCsv = useCallback(() => {
		if (!csvData) {
			return;
		}

		exportToCSV(csvData);
		setExportModalOpen(false);
	}, [csvData]);

	const handleExportXlsx = useCallback(async () => {
		if (!csvData) {
			return;
		}

		setExporting(true);
		try {
			await exportToXLSX(csvData);
			setExportModalOpen(false);
		} finally {
			setExporting(false);
		}
	}, [csvData]);

	return (
		<>
			<Col>
				<Button onClick={handleGerar} icon={<PrinterOutlined />} type="primary">
					Gerar
				</Button>
			</Col>

			{!isEmptyResponse && isRelatorioRequisitado && !noFilter && (
				<>
					<Col>
						<Button icon={<PrinterOutlined />} onClick={handleImprimir} loading={isImpressaoRequisitado}>
							Imprimir
						</Button>
					</Col>

					{registros.length > 0 && csvData && (
						<Col>
							<Button icon={<UploadOutlined />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
						</Col>
					)}

					<Col flex="1 1 100px">{extraButtons}</Col>
				</>
			)}

			<Modal
				title="Exportar relatório"
				open={exportModalOpen}
				onCancel={() => setExportModalOpen(false)}
				footer={[
					<Button key="csv" onClick={handleExportCsv} disabled={exporting}>
						CSV
					</Button>,
					<Button key="xlsx" type="primary" onClick={handleExportXlsx} loading={exporting}>
						Excel (XLSX)
					</Button>,
				]}
			>
				Escolha o formato do arquivo.
			</Modal>
		</>
	);
}
